/**
 * GodModeTooltip — Compact hover popup showing entity key fields.
 *
 * Shows: entity type badge, truncated UUID, status, domain, key timestamp.
 * Anchored below (or above if near viewport bottom). Purple accent.
 * Hidden when the inspection panel is open for the same entity.
 */

import { getEntitySummary, truncateId, formatDate } from '../utils/godModeInspect';
import { useGodMode } from '../context/GodModeContext';

const TYPE_ICONS = {
  annotator: '👤',
  job: '📋',
  application: '📩',
  assignment: '🔗',
};

export default function GodModeTooltip({ type, entity, rect }) {
  const { selectedEntity } = useGodMode();

  if (!entity || !rect) return null;

  // Don't show tooltip if panel is open for the same entity
  if (selectedEntity?.entity?.id === entity?.id && selectedEntity?.type === type) return null;

  const summary = getEntitySummary(type, entity);
  if (!summary) return null;

  // Viewport edge detection — flip above if too close to bottom
  const tooltipHeight = 160;
  const spaceBelow = window.innerHeight - rect.bottom;
  const flipUp = spaceBelow < tooltipHeight + 20;

  const top = flipUp ? rect.top - tooltipHeight - 6 : rect.bottom + 6;
  const left = Math.max(8, Math.min(rect.left, window.innerWidth - 290));

  return (
    <div style={{
      position: 'fixed', top, left, zIndex: 300,
      width: 280, padding: '10px 14px',
      background: '#1a1a2e', color: '#e0e0e0',
      border: '1px solid rgba(175,169,236,0.3)',
      borderRadius: 8,
      boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
      fontFamily: "'DM Mono', monospace", fontSize: 10,
      pointerEvents: 'none',
      animation: 'godTooltipFadeIn 0.12s ease-out',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <span>{TYPE_ICONS[type] || '📦'}</span>
        <span style={{
          fontSize: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em',
          padding: '1px 6px', borderRadius: 3,
          background: 'rgba(175,169,236,0.15)', color: '#AFA9EC',
        }}>{type}</span>
        <span style={{ color: '#fff', fontWeight: 500, fontSize: 11 }}>{summary.name}</span>
      </div>

      {/* Fields */}
      <div style={{ display: 'grid', gridTemplateColumns: '70px 1fr', gap: '3px 8px' }}>
        <span style={{ color: '#666' }}>ID</span>
        <span style={{ color: '#AFA9EC' }}>{truncateId(summary.id)}</span>

        {summary.status && <>
          <span style={{ color: '#666' }}>Status</span>
          <span style={{ color: summary.status === 'Active' ? '#5DCAA5' : summary.status === 'Completed' ? '#85B7EB' : '#F5A623' }}>{summary.status}</span>
        </>}

        {summary.domain && <>
          <span style={{ color: '#666' }}>Domain</span>
          <span>{summary.domain}</span>
        </>}

        {summary.environment && <>
          <span style={{ color: '#666' }}>Env</span>
          <span style={{ color: summary.environment === 'production' ? '#5DCAA5' : '#F5A623' }}>{summary.environment}</span>
        </>}

        {summary.timestamp && <>
          <span style={{ color: '#666' }}>Created</span>
          <span style={{ color: '#888' }}>{formatDate(summary.timestamp).split(' · ')[0]}</span>
        </>}
      </div>

      {/* Hint */}
      <div style={{ marginTop: 6, fontSize: 8, color: '#555', textAlign: 'center' }}>
        Click 👁 to inspect · ESC to close panel
      </div>
    </div>
  );
}
